/**
 * MediaAttachButton — "Add Authenticated Image" for admin record editors.
 * One asset can serve many records: attaching never duplicates the file.
 * Selecting an already-attached asset with a new role updates the role.
 * Attaching never publishes — publication stays owner-only in the Library.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ImagePlus, Loader2, Upload, X, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  mediaList, mediaUpload, mediaAttach, mediaDetach,
} from "../../lib/api";
import { AuthImg } from "./AuthImg";
import { MEDIA_ROLES } from "./mediaRoles";

const STATUS_COLOR = {
  draft: "#8A8578", review: "#E0A526", published: "#3FB950",
  rejected: "#D92D20", archived: "#5A554C",
};

const Chip = ({ status }) => (
  <span className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 shrink-0"
        style={{ background: STATUS_COLOR[status] || "#333", color: "#0a0907" }}>{status}</span>
);

export const MediaAttachButton = ({ token, userToken = null, targetType, targetId, recordName = "" }) => {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("gallery");
  const [q, setQ] = useState("");
  const [assets, setAssets] = useState([]);
  const [attached, setAttached] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lib, mine] = await Promise.all([
        mediaList(token, { limit: 60, ...(q ? { q } : {}) }, userToken),
        mediaList(token, { limit: 60, target_type: targetType, target_id: targetId }, userToken),
      ]);
      setAttached(mine.assets || []);
      const mineIds = new Set((mine.assets || []).map((a) => a.id));
      setAssets((lib.assets || []).filter((a) => !mineIds.has(a.id)));
    } catch { toast.error("Could not load Media Library"); }
    finally { setLoading(false); }
  }, [token, userToken, q, targetType, targetId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doAttach = async (assetId) => {
    setBusy(assetId);
    try {
      await mediaAttach(token, assetId, { target_type: targetType, target_id: targetId, role }, userToken);
      toast.success(`Attached as ${role.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Attach failed"); }
    finally { setBusy(""); }
  };

  const doDetach = async (assetId) => {
    setBusy(assetId);
    try {
      await mediaDetach(token, assetId, { target_type: targetType, target_id: targetId }, userToken);
      load();
    } catch { toast.error("Detach failed"); }
    finally { setBusy(""); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const asset = await mediaUpload(token, f, null, null, userToken);
        await mediaAttach(token, asset.id, { target_type: targetType, target_id: targetId, role }, userToken);
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} uploaded as draft and attached — publish from the Media Library`);
    load();
  };

  const roleOf = (a) => (a.attachments || []).find(
    (x) => x.target_type === targetType && x.target_id === targetId)?.role || "gallery";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
              data-testid="add-authenticated-image-btn"
              className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#A68F5E] text-[#A39E93] hover:text-[#E4DFD3] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors">
        <ImagePlus size={12} /> Add Authenticated Image
        {attached.length > 0 && <span className="text-[#A68F5E]">({attached.length})</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[210] bg-black/80 flex sm:items-center sm:justify-center overflow-y-auto"
             data-testid="media-attach-modal" onClick={() => setOpen(false)}>
          <div className="bg-[#12110e] border border-[#2D2A26] w-full sm:max-w-2xl sm:my-8 min-h-full sm:min-h-0 sm:max-h-[85vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#12110e] border-b border-[#2D2A26] flex items-center gap-2 px-4 py-3 z-10">
              <ImagePlus size={14} className="text-[#A68F5E]" />
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3] truncate">
                  Authenticated Images
                </div>
                <div className="font-mono text-[9px] text-[#757065] truncate">
                  {targetType} · {recordName || targetId}
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="media-attach-modal-close"
                      className="ml-auto w-10 h-10 inline-flex items-center justify-center text-[#A39E93]"><X size={18} /></button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto">
              {/* Role for new attachments */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168]">Attach as</span>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                        data-testid="media-attach-role"
                        className="bg-[#0F0E0B] border border-[#2D2A26] px-2 min-h-[42px] text-[13px] text-[#E4DFD3] focus:outline-none focus:border-[#A68F5E]">
                  {MEDIA_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                </select>
                <label className="inline-flex items-center gap-1.5 border border-[#A68F5E] text-[#A68F5E] px-3 min-h-[42px] font-mono text-[10px] uppercase tracking-[0.2em] cursor-pointer">
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  Upload new
                  <input type="file" accept="image/*,.heic,.heif" multiple className="hidden"
                         onChange={onUpload} data-testid="media-attach-upload-input" disabled={uploading} />
                </label>
              </div>

              {/* Currently attached */}
              {attached.length > 0 && (
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168] mb-2">
                    Attached to this record
                  </div>
                  <div className="space-y-1.5" data-testid="media-attached-list">
                    {attached.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 bg-[#0F0E0B] border border-[#2D2A26] p-2">
                        <AuthImg token={token} userToken={userToken} assetId={a.id}
                                 className="w-12 h-12 object-cover border border-[#2D2A26] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[10px] text-[#E4DFD3] truncate">{a.caption || a.original_filename}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Chip status={a.status} />
                            <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#9FA8D4]">{roleOf(a).replace(/_/g, " ")}</span>
                            <span className="font-mono text-[8px] text-[#5A554C]">{a.verification}</span>
                          </div>
                        </div>
                        <button onClick={() => doAttach(a.id)} disabled={!!busy}
                                title={`Set role to ${role}`} data-testid={`media-attach-setrole-${a.id}`}
                                className="shrink-0 border border-[#454038] text-[#A39E93] px-2 min-h-[38px] font-mono text-[8px] uppercase">
                          set role
                        </button>
                        <button onClick={() => doDetach(a.id)} disabled={!!busy}
                                data-testid={`media-detach-${a.id}`}
                                className="shrink-0 w-9 h-9 inline-flex items-center justify-center text-[#7B7168] hover:text-[#D92D20]">
                          {busy === a.id ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Library picker */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168]">Library</span>
                  <div className="relative flex-1 max-w-[240px]">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#5A554C]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                           data-testid="media-attach-search"
                           className="w-full bg-[#0F0E0B] border border-[#2D2A26] pl-7 pr-2 min-h-[38px] text-[13px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
                  </div>
                </div>
                {loading ? (
                  <div className="py-8 text-center"><Loader2 size={15} className="animate-spin inline text-[#A68F5E]" /></div>
                ) : assets.length === 0 ? (
                  <div className="border border-[#2D2A26] px-3 py-6 text-center font-mono text-[10px] text-[#7B7168]">
                    No other assets in the library. Upload one above.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="media-attach-grid">
                    {assets.map((a) => (
                      <button key={a.id} onClick={() => doAttach(a.id)} disabled={!!busy}
                              data-testid={`media-attach-pick-${a.id}`}
                              className="relative text-left border border-[#2D2A26] bg-[#0F0E0B] overflow-hidden hover:border-[#A68F5E] transition-colors disabled:opacity-50">
                        <div className="aspect-square bg-black">
                          <AuthImg token={token} userToken={userToken} assetId={a.id}
                                   className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute top-1 left-1"><Chip status={a.status} /></div>
                        {busy === a.id && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <Loader2 size={15} className="animate-spin text-[#A68F5E]" />
                          </div>
                        )}
                        <div className="px-1.5 py-1 font-mono text-[8px] text-[#A39E93] truncate flex items-center gap-1">
                          <Link2 size={8} /> {a.caption || a.original_filename}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className="font-mono text-[9px] text-[#5A554C] leading-relaxed border-t border-[#2D2A26] pt-3">
                Attaching never publishes. Only the Owner publishes assets from the
                Media Library — and only published derivatives ever appear publicly.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MediaAttachButton;
